/**
 * MediaItemCard - Card individual de mídia
 * Módulo Motorista - Sistema IDOOH
 */

import React, { useState } from 'react';
import { MoreVertical, Eye, Trash2, Calendar, DollarSign, Image, Video } from 'lucide-react';
import { CampaignMedia } from '../types/dashCompanion';

interface MediaItemCardProps {
    media: CampaignMedia;
    onRemove: (mediaId: string) => void;
    onView?: (media: CampaignMedia) => void;
}

export function MediaItemCard({ media, onRemove, onView }: MediaItemCardProps) {
    const [showMenu, setShowMenu] = useState(false);

    const formatDate = (date: Date) => {
        return new Date(date).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit'
        });
    };

    const getStatusBadge = () => {
        switch (media.status) {
            case 'active':
                return (
                    <span className="px-2 py-0.5 text-xs font-medium bg-emerald-500 text-white rounded-full">
                        Ativo
                    </span>
                );
            case 'pending':
                return (
                    <span className="px-2 py-0.5 text-xs font-medium bg-amber-500 text-white rounded-full">
                        Pendente
                    </span>
                );
            case 'expired':
                return (
                    <span className="px-2 py-0.5 text-xs font-medium bg-red-500 text-white rounded-full">
                        Expirado
                    </span>
                );
        }
    };

    return (
        <div className="bg-white border border-emerald-200 rounded-xl p-3 shadow-sm relative hover:shadow-md hover:border-emerald-300 transition-all">
            <div className="flex gap-3">
                {/* Thumbnail */}
                <div className="relative w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-emerald-100 border-2 border-emerald-300">
                    <img
                        src={media.thumbnailUrl}
                        alt={media.title}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-1 right-1">
                        {media.type === 'video' ? (
                            <div className="bg-emerald-600 rounded-full p-1 shadow-md">
                                <Video className="w-3 h-3 text-white" />
                            </div>
                        ) : (
                            <div className="bg-emerald-600 rounded-full p-1 shadow-md">
                                <Image className="w-3 h-3 text-white" />
                            </div>
                        )}
                    </div>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 truncate">{media.title}</p>
                            <p className="text-xs text-emerald-600 font-mono mt-0.5 font-medium">ID: {media.id}</p>
                        </div>
                        {getStatusBadge()}
                    </div>

                    {/* Datas */}
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3 text-emerald-600" />
                            <span>{formatDate(media.startDate)}</span>
                        </div>
                        <span className="text-emerald-500 font-bold">→</span>
                        <span>{formatDate(media.endDate)}</span>
                    </div>

                    {/* Ganhos */}
                    {media.estimatedEarnings && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-emerald-700 font-bold">
                            <DollarSign className="w-3 h-3" />
                            <span>R$ {media.estimatedEarnings.toFixed(2)}</span>
                        </div>
                    )}
                </div>

                {/* Menu 3 pontinhos */}
                <div className="relative">
                    <button
                        onClick={() => setShowMenu(!showMenu)}
                        className="p-1.5 hover:bg-emerald-100 rounded-lg transition-colors"
                    >
                        <MoreVertical className="w-5 h-5 text-emerald-600" />
                    </button>

                    {showMenu && (
                        <>
                            <div
                                className="fixed inset-0 z-10"
                                onClick={() => setShowMenu(false)}
                            />
                            <div className="absolute right-0 top-8 z-20 bg-white rounded-lg shadow-xl border border-emerald-200 py-1 min-w-[140px]">
                                <button
                                    onClick={() => {
                                        onView?.(media);
                                        setShowMenu(false);
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-emerald-50"
                                >
                                    <Eye className="w-4 h-4 text-emerald-600" />
                                    Ver detalhes
                                </button>
                                <button
                                    onClick={() => {
                                        onRemove(media.id);
                                        setShowMenu(false);
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Remover
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default MediaItemCard;
